<template>
  <v-card>
    <div class="d-flex align-center py-4 py-lg-2 py-xl-4 px-2">
      <h4 class="secondary--text font-weight-bold ml-6">
        {{ $t('fleet_management.geofences.create_geofence.new_geofence') }}
      </h4>
    </div>
    <v-divider></v-divider>
    <v-form ref="form" v-model="valid" v-if="showForm">
      <v-container class="px-8 px-lg-8 px-xl-8 pb-6 pt-4">
        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between">
              <span class="primary--text text-caption">{{
                $t(
                  'fleet_management.geofences.create_geofence.rules.fields_required'
                )
              }}</span>
            </div>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="auto" class="mt-4">
            <v-text-field
              id="idZonasNombreZona"
              v-model.trim="geofenceName"
              @blur="checkName"
              :counter="40"
              :error-messages="errorName"
              :rules="nameRules"
              :label="
                $t(
                  'fleet_management.geofences.create_geofence.labels.new_geofence_name'
                )
              "
              filled
              dense
              required
              color="tertiary"
              ref="geofenceName"
            />
          </v-col>
          <v-col cols="3" class="mt-4 ml-4">
            <v-select
              v-model="selected"
              :items="items"
              return-object
              dense
              filled
              label="Tipo"
              item-text="name"
              color="tertiary"
              @change="handleResetZone"
              @input="typeGeofenceInp"
            >
              <template slot="item" slot-scope="data">
                <SvgIcon
                  class="secondary--text mr-1"
                  :iconType="data.item.icon"
                />
                <span
                  :id="
                    data.item.name === 'Circular'
                      ? 'idZonasCircular'
                      : 'idZonasPoligonal'
                  "
                >
                  {{ data.item.name }}
                </span>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <hr class="divider-dotted" />
          </v-col>
        </v-row>
        <component
          v-bind:is="selected.component"
          @circleEmit="
            data => {
              circleEmit(data)
            }
          "
          @polygonEmit="
            data => {
              polygonEmit(data)
            }
          "
          @resetZone="handleResetZone"
        ></component>
        <v-row>
          <v-col class="my-3">
            <v-row justify="space-between">
              <v-col cols="auto">
                <v-btn
                  id="idBtnZonasCancelarCreacion"
                  @click="cancel"
                  outlined="true"
                  color="secondary"
                  rounded
                  >{{
                    $t(
                      'fleet_management.geofences.create_geofence.labels.form_new_geofence_cancel_button'
                    )
                  }}</v-btn
                >
              </v-col>
              <v-col cols="auto">
                <v-btn
                  id="idBtnZonasGuardarZona"
                  color="tertiary white--text"
                  rounded
                  :loading="this.loading"
                  :disabled="!geofenceValid || !valid"
                  @click="saveZone"
                  >{{
                    $t(
                      'fleet_management.geofences.create_geofence.labels.form_new_geofence_accept_button'
                    )
                  }}</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <div v-else>
      <v-row>
        <v-col cols="12">
          <h2 class="secondary--text text-center mt-9">
            {{
              $t(
                'fleet_management.geofences.create_geofence.alerts.geofence_created'
              )
            }}
          </h2>
        </v-col>
        <v-col>
          <h4 class="secondary--text text-center">
            {{ geofenceName }}
          </h4>
        </v-col>
        <v-col cols="12" class="d-flex justify-center px-7 my-16">
          <v-row class="text-center">
            <v-col md="6">
              <v-btn
                id="idBtnZonasVerDetalleDeLaZona"
                outlined="true"
                color="secondary"
                rounded
                :to="{ name: 'GeofenceDetail', params: { id: newGeofenceId } }"
                >{{
                  $t(
                    'fleet_management.geofences.create_geofence.labels.geofence_created_action_detail_button'
                  )
                }}</v-btn
              >
            </v-col>
            <v-col md="6">
              <v-btn
                id="idBtnZonasIrAListado"
                color="tertiary white--text"
                rounded
                :to="{ name: 'GeofencesList' }"
                >{{
                  $t(
                    'fleet_management.geofences.create_geofence.labels.geofence_created_action_list_button'
                  )
                }}</v-btn
              >
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <Dialog
      idCancelButton="idBtnNoVolver"
      idSubmitButton="idBtnSiCancelar"
      :closeButtonText="dialog.closeButtonText"
      @cancelButtonPressed="showDialog = false"
      :oneButton="dialog.oneButton"
      @oneButtonAction="cancelDialog"
      :oneButtonText="dialog.oneButtonText"
      :showDialog="showDialog"
      @submitButtonPressed="cancelGeofence"
      :submitButtonText="dialog.acceptButtonText"
      :text="dialog.text"
      :type="dialog.type"
    />
  </v-card>
</template>

<script>
import { GEOFENCE_MIN_RADIUS, GEOFENCE_MAX_RADIUS } from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'
import { GeofencesHelpers, MapHelpers } from '@/helpers/mainExports.js'
import Dialog from '@/components/dialog/Dialog'
import { mapActions } from 'vuex'
import CircleZone from './CircleZone.vue'
import PolygonZone from './PolygonZone.vue'
import { SvgIcon, firebaseServices } from '@leapsight/styleguide'
import { FirebaseTagNames } from '@leapsight/portal'

export default {
  name: 'CreateZone',
  components: {
    Dialog,
    CircleZone,
    PolygonZone,
    SvgIcon
  },
  props: {
    zonesList: {
      type: Array,
      default: []
    }
  },
  data() {
    return {
      selected: [
        {
          name: {},
          component: '',
          icon: ''
        }
      ],
      items: [
        {
          name: 'Circular',
          component: 'CircleZone',
          icon: 'icon_geofence_circle'
        },
        {
          name: 'Poligonal',
          component: 'PolygonZone',
          icon: 'icon_geofence_polygon'
        }
      ],
      geofencePayload: null,
      coordinatesRules: {
        latitude: value => {
          const regex = /^(\+|-)?(?:90(?:(?:\.0{1,7})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,7})?))$/
          const result = regex.test(value)
          //Si no cumple con el formato de coordenada, verifico que sea un número real lo ingresado
          if (!result) {
            const isNumberRegex = /^-?[0-9]\d*(\.\d+)?$/
            const isNumber = isNumberRegex.test(value)
            if (isNumber && value > 90)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.max_latitude'
              )
            else if (isNumber && value < -90)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.min_latitude'
              )
            else if (!isNumber)
              //No es un número
              return i18n.t('fleet_management.geofences.coordinates.bad_format')
          }
          return result
        },
        longitude: value => {
          const regex = /^(\+|-)?(?:180(?:(?:\.0{1,7})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,7})?))$/
          const result = regex.test(value)
          if (!result) {
            const isNumberRegex = /^-?[0-9]\d*(\.\d+)?$/
            const isNumber = isNumberRegex.test(value)
            if (isNumber && value > 180)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.max_longitude'
              )
            else if (isNumber && value < -180)
              return i18n.t(
                'fleet_management.geofences.create_geofence.rules.min_longitude'
              )
            else if (!isNumber)
              //No es un número
              return i18n.t('fleet_management.geofences.coordinates.bad_format')
          }
          return result
        }
      },
      dialogDefault: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      dialog: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      errorName: '',
      geofenceName: '',
      geofenceValid: false,
      geofenceCreatedSuccessful: false,
      longitude: null,
      latitude: null,
      loading: false,
      maxRadius: GEOFENCE_MAX_RADIUS,
      minRadius: GEOFENCE_MIN_RADIUS,
      newGeofenceId: '',
      nameRules: [
        v =>
          !!v ||
          i18n.t('fleet_management.persons.new_person.rules.name_required'),
        v =>
          (v && v.length >= 2 && v.length <= 40) ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.character_length'
          ),
        v =>
          /^[a-zA-Z0-9áéíóúüÁÉÍÓÚÜñÑçÇâêîôûÂÊÎÔÛàèìòùÀÈÌÒÙäöüÄÖÜßëïÿËÏŸ#$^\[\]&_ -.\\s]+$/gi.test(
            v
          ) || i18n.t('fleet_management.agents.create.errors.invalid_name')
      ],
      cancelSubmit: false,
      radius: null,
      radiusError: '',
      radiusRules: {
        required: value =>
          !!value ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.field_required'
          ),
        range: value => {
          if (value > GEOFENCE_MAX_RADIUS)
            return i18n.t(
              'fleet_management.geofences.circular_geofence.rules.max_radius',
              {
                GEOFENCE_MAX_RADIUS
              }
            )
          else if (value < GEOFENCE_MIN_RADIUS)
            return i18n.t(
              'fleet_management.geofences.circular_geofence.rules.min_radius',
              {
                GEOFENCE_MIN_RADIUS
              }
            )
          else return !!value
        },
        format: value => {
          const regex = /^\d+$/
          return (
            regex.test(value) ||
            i18n.t('fleet_management.geofences.coordinates.bad_format')
          )
        }
      },
      routeTo: { name: 'GeofencesList' },
      showForm: true,
      showDialog: false,
      valid: true
    }
  },
  methods: {
    ...mapActions('ui', ['setSnackbar']),
    ...mapActions(['getGeofences']),
    typeGeofenceInp() {
      firebaseServices.logEvent(
        firebaseServices.analytics,
        FirebaseTagNames.INP_FLEET_MANAGEMENT_TYPE_ZONE
      )
    },
    cancel() {
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_text_cancel_new_geofence'
      )
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_cancel_button'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_accept_button'
      )
    },
    cancelDialog() {
      this.showDialog = false
      Object.assign(this.dialog, this.dialogDefault)
    },
    cancelGeofence() {
      this.geofenceCreatedSuccessful = true
      this.$router.push({ name: this.routeTo.name })
    },
    handleResetZone() {
      this.geofencePayload = null
      this.geofenceValid = null
    },
    circleEmit(data) {
      this.geofencePayload = data[0]
      this.geofenceValid = data[1]
    },
    polygonEmit(data) {
      this.geofencePayload = data[0]
      this.geofenceValid = data[1]
    },
    checkName() {
      this.errorName = ''
      this.cancelSubmit = false
      const name = this.geofenceName
      const zones = this.zonesList

      if (zones.length > 0) {
        for (let index = 0; index < zones.length; index++) {
          const element = zones[index]
          if (element.properties.name === name) {
            this.showDialog = true
            this.dialog.type = 'warning'
            this.dialog.text = i18n.t(
              'fleet_management.geofences.create_geofence.errors.name_match'
            )

            this.dialog.oneButton = true
            this.dialog.oneButtonText = i18n.t(
              'general.dialogComponent.acceptButtonText'
            )
            this.errorName = i18n.t(
              'fleet_management.geofences.create_geofence.errors.name_match'
            )
            this.cancelSubmit = true
            break
          }
        }
      }
    },
    async preventDuplicateNameOnSubmit() {
      try {
        if (this.zonesList.length > 0) this.checkName()
        else {
          const response = await GeofencesHelpers.getGeofences()
          if (!response.hasOwnProperty('error')) {
            const zones = response.map(item => {
              return item.data
            })
            this.zonesList = zones
            this.checkName()
          } else {
            //Mostrar snackbar de error
            this.cancelSubmit = true
            const errorText = GeofencesHelpers.getErrorMessageByCode(
              response.error
            )
            const snackbar = {
              text: errorText
            }
            this.setSnackbar(snackbar)
          }
        }
      } catch (error) {
        //Mostrar snackbar de error
        this.cancelSubmit = true
        const snackbar = {
          text:
            'No se pudo conectar con el servicio por favor intente mas tarde'
        }
        this.setSnackbar(snackbar)
      }
    },

    async saveZone() {
      //Antes de guardar obtenemos el listado de geofensas actualizado y comparamos el valor ingresado para ver si existe
      await this.preventDuplicateNameOnSubmit()
      if (
        this.$refs.form.validate() &&
        this.geofencePayload !== null &&
        !this.cancelSubmit
      ) {
        this.loading = true
        //se agrega la propiedad "name" antes de mandar el payload
        this.geofencePayload.properties.name = this.geofenceName
        const payload = this.geofencePayload

        GeofencesHelpers.createGeofence(payload)
          .then(response => {
            this.newGeofenceId = response.args[0].id
            this.geofenceCreatedSuccessful = true
            this.loading = false
            this.showForm = false
          })
          .catch(error => {
            this.dialog.loading = false
            this.showDialog = false
            const errorText = GeofencesHelpers.getErrorMessageByCode(
              error.error
            )
            const snackbar = {
              text: errorText
            }
            store.dispatch('ui/setSnackbar', snackbar)
          })
      }
    }
  },
  mounted() {
    document.getElementsByClassName('v-select__slot')[0].id = 'idZonasTipo'
    this.$nextTick(function() {
      const map = GeofencesHelpers.getMap()

      MapHelpers.setMapCenterBaseOnCountryId()
    })
  },

  beforeRouteLeave(to, from, next) {
    if (this.geofenceCreatedSuccessful) {
      next()
    } else {
      this.routeTo = to
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_text_cancel_new_geofence'
      )
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_cancel_button'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.geofences.create_geofence.alerts.card_accept_button'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.orange--text {
  color: var(--orange) !important;
}
.dialog-attention {
  border: 2px solid var(--orange);
}
</style>
